import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaInfoCircle } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import LoadingOverlay from './LoadingOverlay';
import TextModal from './TextModal';
import { useToast } from './ToastContext';
import CustomSelect from './CustomSelect';
import './../Styles/Employee.css';

const apiUrl = process.env.REACT_APP_API_URL;

const RATING_OPTIONS = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' }
];

const COMMUNICATED_OPTIONS = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
];

const GoalAssessmentTable = ({ 
    employeeId: propEmployeeId,
    onGoalSubmitted 
}) => {
    const showToast = useToast();
    const [assignedGoals, setAssignedGoals] = useState([]);
    const [submittedGoals, setSubmittedGoals] = useState({}); // { [quarter]: { [gId]: true } }
    const [quarters, setQuarters] = useState([]);
    const [selectedQuarter, setSelectedQuarter] = useState('');
    const [goalDescriptions, setGoalDescriptions] = useState({}); // { [quarter]: { [gId]: text } }
    const [attachmentLinks, setAttachmentLinks] = useState({});   // { [quarter]: { [gId]: text } }
    const [selfRatings, setSelfRatings] = useState({});           // { [quarter]: { [gId]: rating } }
    const [communicatedStatuses, setCommunicatedStatuses] = useState({}); // { [quarter]: { [gId]: comm } }
    const [savedGoals, setSavedGoals] = useState({});             // { [quarter]: { [gId]: true } }
    const [errors, setErrors] = useState({});
    const [loadingGoalId, setLoadingGoalId] = useState(null);
    const [isInstructionsOpen, setInstructionsOpen] = useState(false);
    const [infoGoal, setInfoGoal] = useState(null);

    // TextModal state
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentField, setCurrentField] = useState(null);
    const [currentGoalId, setCurrentGoalId] = useState(null);
    const [initialText, setInitialText] = useState('');

    const activeEmployeeId = propEmployeeId || localStorage.getItem('employeeId');

    // Fetch Quarters
    useEffect(() => {
        const defaultQuarters = [
            { id: 'q1', quarter: 'Q1 2024' },
            { id: 'q2', quarter: 'Q2 2024' },
            { id: 'q3', quarter: 'Q3 2024' },
            { id: 'q4', quarter: 'Q4 2024' }
        ];

        axios.get(`${apiUrl}/api/quarters`)
            .then(res => {
                const data = res.data || [];
                if (data.length > 0) {
                    const qs = data.map(q => typeof q === 'string' ? q : (q.quarter || q.quarter_name || q.name || ''));
                    setQuarters(qs);
                } else {
                    setQuarters(defaultQuarters.map(q => q.quarter));
                }
            })
            .catch(err => {
                console.error('Error fetching quarters:', err);
                setQuarters(defaultQuarters.map(q => q.quarter));
            });
    }, []);

    // Fetch Assigned Goals
    useEffect(() => {
        if (!activeEmployeeId) return;

        axios.get(`${apiUrl}/api/employee-goals/${activeEmployeeId}`)
            .then(res => {
                // Ensure distinct list by goal_id or id
                const goalsList = res.data || [];
                const distinctMap = new Map();
                goalsList.forEach(g => {
                    const key = (g.goal_id ? g.goal_id.toString() : (g._id || g.id)).toString();
                    if (!distinctMap.has(key)) {
                        distinctMap.set(key, g);
                    }
                });
                setAssignedGoals(Array.from(distinctMap.values()));
            })
            .catch(err => console.error('Error fetching employee goals:', err));
    }, [activeEmployeeId]);

    // Helper: find submission or draft for this goal in the selected quarter
    const getQuarterRecord = (goal, q) => {
        if (!goal || !q) return null;
        if (Array.isArray(goal.submissions)) {
            const submitted = goal.submissions.find(s => s.quarter === q && s.is_submitted);
            if (submitted) return submitted;
            const draft = goal.submissions.find(s => s.quarter === q);
            if (draft) return draft;
        }
        if (goal.quarter === q) {
            return goal;
        }
        return null;
    };

    const getQuarterSubmission = (goal, q) => {
        const rec = getQuarterRecord(goal, q);
        return (rec && rec.is_submitted) ? rec : null;
    };

    // Modal controls
    const openModal = (field, goalId, existingText, goal) => {
        if (!selectedQuarter) {
            showToast('Please select a quarter first', 'warning');
            return;
        }
        const quarterRecord = goal ? getQuarterRecord(goal, selectedQuarter) : null;
        setCurrentField(field);
        setCurrentGoalId(goalId);
        if (field === 'description') {
            const currentText = goalDescriptions[selectedQuarter]?.[goalId] !== undefined
                ? goalDescriptions[selectedQuarter][goalId]
                : (quarterRecord?.self_assessment || existingText || '');
            setInitialText(currentText);
        } else if (field === 'attachment') {
            const currentText = attachmentLinks[selectedQuarter]?.[goalId] !== undefined
                ? attachmentLinks[selectedQuarter][goalId]
                : (quarterRecord?.attachments || existingText || '');
            setInitialText(currentText);
        }
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setInitialText('');
    };

    const handleTextSubmit = (submittedText) => {
        if (!selectedQuarter) {
            showToast('Please select a quarter first', 'warning');
            closeModal();
            return;
        }
        if (currentField === 'description' && currentGoalId !== null) {
            setGoalDescriptions(prev => ({
                ...prev,
                [selectedQuarter]: {
                    ...(prev[selectedQuarter] || {}),
                    [currentGoalId]: submittedText
                }
            }));
            if (submittedText.trim()) {
                setErrors(prev => {
                    const next = { ...prev };
                    delete next[`${currentGoalId}_description`];
                    return next;
                });
            }
        } else if (currentField === 'attachment' && currentGoalId !== null) {
            setAttachmentLinks(prev => ({
                ...prev,
                [selectedQuarter]: {
                    ...(prev[selectedQuarter] || {}),
                    [currentGoalId]: submittedText
                }
            }));
        }
        closeModal();
    };

    // Save row (persists draft to MongoDB)
    const isSaveEnabled = (goal, goalId) => {
        if (!selectedQuarter) return false;
        const quarterRecord = getQuarterRecord(goal, selectedQuarter);
        const desc = goalDescriptions[selectedQuarter]?.[goalId] !== undefined
            ? goalDescriptions[selectedQuarter][goalId]
            : (quarterRecord?.self_assessment || '');
        const rating = selfRatings[selectedQuarter]?.[goalId] !== undefined
            ? selfRatings[selectedQuarter][goalId]
            : (quarterRecord?.self_rating ? String(quarterRecord.self_rating) : '');
        const comm = communicatedStatuses[selectedQuarter]?.[goalId] !== undefined
            ? communicatedStatuses[selectedQuarter][goalId]
            : (quarterRecord?.communicated || '');
        return (desc || '').trim() !== '' || rating !== '' || comm !== '';
    };

    const handleSaveRow = async (goal) => {
        const goalId = (goal.goal_id ? goal.goal_id.toString() : (goal._id || goal.id)).toString();
        if (!selectedQuarter) {
            showToast('Please select a quarter before saving', 'warning');
            return;
        }

        const quarterRecord = getQuarterRecord(goal, selectedQuarter);
        const description = (
            goalDescriptions[selectedQuarter]?.[goalId] !== undefined
                ? goalDescriptions[selectedQuarter][goalId]
                : (quarterRecord?.self_assessment || '')
        );

        const rating = (
            selfRatings[selectedQuarter]?.[goalId] !== undefined
                ? selfRatings[selectedQuarter][goalId]
                : (quarterRecord?.self_rating ? String(quarterRecord.self_rating) : '')
        );

        const communicated = (
            communicatedStatuses[selectedQuarter]?.[goalId] !== undefined
                ? communicatedStatuses[selectedQuarter][goalId]
                : (quarterRecord?.communicated || '')
        );

        const attachment = (
            attachmentLinks[selectedQuarter]?.[goalId] !== undefined
                ? attachmentLinks[selectedQuarter][goalId]
                : (quarterRecord?.attachments || '')
        );

        setLoadingGoalId(goalId);
        try {
            await axios.post(`${apiUrl}/api/update-goal-row`, {
                goalId: goal.goal_id ? goal.goal_id.toString() : goalId,
                self_assessment: description,
                attachments: attachment,
                self_rating: rating,
                communicated: communicated,
                employeeId: activeEmployeeId,
                quarter: selectedQuarter,
                is_submitted: false
            });

            // Update assignedGoals submissions array locally so it reflects the saved draft
            setAssignedGoals(prev => prev.map(g => {
                const curId = (g.goal_id ? g.goal_id.toString() : (g._id || g.id)).toString();
                if (curId === goalId) {
                    const existingSubs = Array.isArray(g.submissions) ? [...g.submissions] : [];
                    const filtered = existingSubs.filter(s => s.quarter !== selectedQuarter);
                    filtered.push({
                        quarter: selectedQuarter,
                        is_submitted: false,
                        self_assessment: description,
                        attachments: attachment,
                        self_rating: rating,
                        communicated: communicated,
                        submitted_at: null
                    });
                    return { ...g, submissions: filtered };
                }
                return g;
            }));

            setSavedGoals(prev => ({
                ...prev,
                [selectedQuarter]: {
                    ...(prev[selectedQuarter] || {}),
                    [goalId]: true
                }
            }));

            showToast('Goal saved successfully.', 'success');
        } catch (error) {
            console.error('Error saving goal draft:', error);
            const errMsg = error.response?.data?.message || error.response?.data?.error || error.response?.data || 'Failed to save goal draft.';
            showToast(typeof errMsg === 'string' ? errMsg : 'Failed to save goal draft.', 'error');
        } finally {
            setLoadingGoalId(null);
        }
    };

    const handleEditRow = (goal, goalId) => {
        if (!selectedQuarter) return;
        const quarterRecord = getQuarterRecord(goal, selectedQuarter);

        // Pre-fill state from saved draft if not currently modified in state
        if (goalDescriptions[selectedQuarter]?.[goalId] === undefined && quarterRecord?.self_assessment) {
            setGoalDescriptions(prev => ({
                ...prev,
                [selectedQuarter]: {
                    ...(prev[selectedQuarter] || {}),
                    [goalId]: quarterRecord.self_assessment
                }
            }));
        }
        if (attachmentLinks[selectedQuarter]?.[goalId] === undefined && quarterRecord?.attachments) {
            setAttachmentLinks(prev => ({
                ...prev,
                [selectedQuarter]: {
                    ...(prev[selectedQuarter] || {}),
                    [goalId]: quarterRecord.attachments
                }
            }));
        }
        if (selfRatings[selectedQuarter]?.[goalId] === undefined && quarterRecord?.self_rating) {
            setSelfRatings(prev => ({
                ...prev,
                [selectedQuarter]: {
                    ...(prev[selectedQuarter] || {}),
                    [goalId]: String(quarterRecord.self_rating)
                }
            }));
        }
        if (communicatedStatuses[selectedQuarter]?.[goalId] === undefined && quarterRecord?.communicated) {
            setCommunicatedStatuses(prev => ({
                ...prev,
                [selectedQuarter]: {
                    ...(prev[selectedQuarter] || {}),
                    [goalId]: quarterRecord.communicated
                }
            }));
        }

        setSavedGoals(prev => ({
            ...prev,
            [selectedQuarter]: {
                ...(prev[selectedQuarter] || {}),
                [goalId]: false
            }
        }));
    };

    // Submit single goal row
    const handleSubmitRow = async (goal) => {
        const goalId = (goal.goal_id ? goal.goal_id.toString() : (goal._id || goal.id)).toString();

        if (!selectedQuarter) {
            showToast('Please select a quarter before submitting', 'warning');
            return;
        }

        // Check if already submitted for this specific quarter
        const existingSub = getQuarterSubmission(goal, selectedQuarter);
        if (existingSub || (submittedGoals[selectedQuarter] && submittedGoals[selectedQuarter][goalId])) {
            showToast('You have already submitted this goal for this quarter.', 'error');
            return;
        }

        const quarterRecord = getQuarterRecord(goal, selectedQuarter);
        const description = (
            goalDescriptions[selectedQuarter]?.[goalId] !== undefined
                ? goalDescriptions[selectedQuarter][goalId]
                : (quarterRecord?.self_assessment || '')
        ).trim();

        const rating = (
            selfRatings[selectedQuarter]?.[goalId] !== undefined
                ? selfRatings[selectedQuarter][goalId]
                : (quarterRecord?.self_rating ? String(quarterRecord.self_rating) : '')
        );

        const communicated = (
            communicatedStatuses[selectedQuarter]?.[goalId] !== undefined
                ? communicatedStatuses[selectedQuarter][goalId]
                : (quarterRecord?.communicated || '')
        );

        const attachment = (
            attachmentLinks[selectedQuarter]?.[goalId] !== undefined
                ? attachmentLinks[selectedQuarter][goalId]
                : (quarterRecord?.attachments || '')
        );

        let newErrors = { ...errors };
        let hasError = false;

        if (!description) {
            newErrors[`${goalId}_description`] = 'Description text is required';
            showToast('Description text is mandatory for submission.', 'warning');
            hasError = true;
        } else {
            delete newErrors[`${goalId}_description`];
        }

        if (!rating) {
            newErrors[`${goalId}_rating`] = 'Self rating is required';
            showToast('Self Rating is mandatory for submission.', 'warning');
            hasError = true;
        } else {
            delete newErrors[`${goalId}_rating`];
        }

        if (!communicated) {
            newErrors[`${goalId}_communicated`] = 'Communicated status is required';
            showToast('Communicated status is mandatory for submission.', 'warning');
            hasError = true;
        } else {
            delete newErrors[`${goalId}_communicated`];
        }

        if (hasError) {
            setErrors(newErrors);
            return;
        }

        setErrors(prev => {
            const next = { ...prev };
            delete next[`${goalId}_description`];
            delete next[`${goalId}_rating`];
            delete next[`${goalId}_communicated`];
            return next;
        });

        setLoadingGoalId(goalId);
        try {
            await axios.post(`${apiUrl}/api/update-goal-row`, {
                goalId: goal.goal_id ? goal.goal_id.toString() : goalId,
                self_assessment: description,
                attachments: attachment,
                self_rating: rating,
                communicated: communicated,
                employeeId: activeEmployeeId,
                quarter: selectedQuarter,
                is_submitted: true
            });

            // Update local submissions list for this goal
            setAssignedGoals(prev => prev.map(g => {
                const curId = (g.goal_id ? g.goal_id.toString() : (g._id || g.id)).toString();
                if (curId === goalId) {
                    const existingSubs = Array.isArray(g.submissions) ? [...g.submissions] : [];
                    const filtered = existingSubs.filter(s => s.quarter !== selectedQuarter);
                    filtered.push({
                        quarter: selectedQuarter,
                        is_submitted: true,
                        self_assessment: description,
                        attachments: attachment,
                        self_rating: rating,
                        communicated: communicated,
                        submitted_at: new Date()
                    });
                    return { ...g, submissions: filtered };
                }
                return g;
            }));

            // Mark this goal as submitted in this quarter
            setSubmittedGoals(prev => ({
                ...prev,
                [selectedQuarter]: {
                    ...(prev[selectedQuarter] || {}),
                    [goalId]: true
                }
            }));

            // Also lock saved status for this quarter
            setSavedGoals(prev => ({
                ...prev,
                [selectedQuarter]: {
                    ...(prev[selectedQuarter] || {}),
                    [goalId]: true
                }
            }));

            showToast('Goal submitted successfully.', 'success');
            if (onGoalSubmitted) {
                onGoalSubmitted(goalId);
            }
        } catch (error) {
            const errMsg = error.response?.data?.message || error.response?.data?.error || error.response?.data || 'You have already submitted this goal for this quarter.';
            showToast(typeof errMsg === 'string' ? errMsg : 'You have already submitted this goal for this quarter.', 'error');
        } finally {
            setLoadingGoalId(null);
        }
    };

    return (
        <div className="employee-body">
            {loadingGoalId && <LoadingOverlay />}
            <div className="goals-header-container">
                <h2>Assigned Goals</h2>
                <div className="quarter-history-container">
                    <div className="quarter-selector">
                        <label htmlFor="quarter">
                            Select Quarter <span className="required-star" style={{ color: 'red' }}>*</span>:
                        </label>
                        <CustomSelect
                            value={selectedQuarter}
                            onChange={(val) => setSelectedQuarter(val)}
                            options={quarters.map(q => ({ value: q, label: q }))}
                            placeholder="Select Quarter"
                            style={{ minWidth: '180px' }}
                        />
                    </div>
                    <button 
                        className="instructions-btn" 
                        onClick={() => setInstructionsOpen(true)} 
                        title="Instructions"
                    >
                        <FaInfoCircle size={24} />
                    </button>
                </div>
            </div>

            <div className="table-container pms-table-wrap">
                <table className="goals-table pms-table">
                    <thead>
                        <tr>
                            <th>GOAL DESCRIPTION</th>
                            <th>DESCRIPTION TEXT <span className="required-star" style={{ color: 'red' }}>*</span></th>
                            <th>ATTACHMENT</th>
                            <th>SELF RATING <span className="required-star" style={{ color: 'red' }}>*</span></th>
                            <th>COMMUNICATED <span className="required-star" style={{ color: 'red' }}>*</span></th>
                            <th>SAVE</th>
                            <th>SUBMIT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {assignedGoals.length > 0 ? (
                            assignedGoals.map((goal) => {
                                const gId = (goal.goal_id ? goal.goal_id.toString() : (goal._id || goal.id)).toString();
                                const quarterRecord = selectedQuarter ? getQuarterRecord(goal, selectedQuarter) : null;
                                const isGoalSubmitted = Boolean(
                                    quarterRecord?.is_submitted ||
                                    (selectedQuarter && submittedGoals[selectedQuarter] && submittedGoals[selectedQuarter][gId])
                                );
                                const isRowSaved = Boolean(
                                    isGoalSubmitted ||
                                    (selectedQuarter && savedGoals[selectedQuarter] && savedGoals[selectedQuarter][gId] !== undefined
                                        ? savedGoals[selectedQuarter][gId]
                                        : Boolean(quarterRecord && (quarterRecord.self_assessment || quarterRecord.self_rating || quarterRecord.communicated)))
                                );

                                // Displayed values
                                const displayDesc = goalDescriptions[selectedQuarter]?.[gId] !== undefined
                                    ? goalDescriptions[selectedQuarter][gId]
                                    : (quarterRecord?.self_assessment || '');

                                const displayAttach = attachmentLinks[selectedQuarter]?.[gId] !== undefined
                                    ? attachmentLinks[selectedQuarter][gId]
                                    : (quarterRecord?.attachments || '');

                                const displayRating = selfRatings[selectedQuarter]?.[gId] !== undefined
                                    ? selfRatings[selectedQuarter][gId]
                                    : (quarterRecord?.self_rating ? String(quarterRecord.self_rating) : '');

                                const displayComm = communicatedStatuses[selectedQuarter]?.[gId] !== undefined
                                    ? communicatedStatuses[selectedQuarter][gId]
                                    : (quarterRecord?.communicated || '');

                                const commFormatted = displayComm === 'yes' ? 'Yes' : displayComm === 'no' ? 'No' : (displayComm || '—');

                                return (
                                    <tr key={gId}>
                                        <td style={{ fontWeight: '500' }}>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                <span>{goal.goal_name || goal.goal_description || 'N/A'}</span>
                                                <button
                                                    type="button"
                                                    className="goal-info-btn"
                                                    onClick={() => setInfoGoal(goal)}
                                                    title="View Goal Details"
                                                    aria-label="View Goal Details"
                                                >
                                                    <FiInfo size={16} />
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            {isRowSaved ? (
                                                <span>{displayDesc || '—'}</span>
                                            ) : (
                                                <>
                                                    <input
                                                        type="text"
                                                        value={displayDesc}
                                                        onChange={(e) => {
                                                            if (!selectedQuarter) {
                                                                showToast('Please select a quarter first', 'warning');
                                                                return;
                                                            }
                                                            const val = e.target.value;
                                                            setGoalDescriptions(prev => ({
                                                                ...prev,
                                                                [selectedQuarter]: {
                                                                    ...(prev[selectedQuarter] || {}),
                                                                    [gId]: val
                                                                }
                                                            }));
                                                            if (val.trim()) {
                                                                setErrors(prev => {
                                                                    const next = { ...prev };
                                                                    delete next[`${gId}_description`];
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        onClick={() => openModal('description', gId, displayDesc, goal)}
                                                        placeholder="Enter description"
                                                    />
                                                    {errors[`${gId}_description`] && (
                                                        <span className="error-message">{errors[`${gId}_description`]}</span>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {isRowSaved ? (
                                                displayAttach ? (
                                                    <a href={displayAttach} target="_blank" rel="noopener noreferrer">
                                                        View Attachment
                                                    </a>
                                                ) : (
                                                    <span>—</span>
                                                )
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={displayAttach}
                                                    onChange={(e) => {
                                                        if (!selectedQuarter) {
                                                            showToast('Please select a quarter first', 'warning');
                                                            return;
                                                        }
                                                        const val = e.target.value;
                                                        setAttachmentLinks(prev => ({
                                                            ...prev,
                                                            [selectedQuarter]: {
                                                                ...(prev[selectedQuarter] || {}),
                                                                [gId]: val
                                                            }
                                                        }));
                                                    }}
                                                    onClick={() => openModal('attachment', gId, displayAttach, goal)}
                                                    placeholder="Enter attachment link"
                                                />
                                            )}
                                        </td>
                                        <td>
                                            {isRowSaved ? (
                                                <span>{displayRating || '—'}</span>
                                            ) : (
                                                <>
                                                    <CustomSelect
                                                        value={displayRating}
                                                        onChange={(val) => {
                                                            if (!selectedQuarter) {
                                                                showToast('Please select a quarter first', 'warning');
                                                                return;
                                                            }
                                                            setSelfRatings(prev => ({
                                                                ...prev,
                                                                [selectedQuarter]: {
                                                                    ...(prev[selectedQuarter] || {}),
                                                                    [gId]: val
                                                                }
                                                            }));
                                                            if (val) {
                                                                setErrors(prev => {
                                                                    const next = { ...prev };
                                                                    delete next[`${gId}_rating`];
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        options={RATING_OPTIONS}
                                                        placeholder="Select"
                                                        error={!!errors[`${gId}_rating`]}
                                                    />
                                                    {errors[`${gId}_rating`] && (
                                                        <span className="error-message">{errors[`${gId}_rating`]}</span>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {isRowSaved ? (
                                                <span>{commFormatted}</span>
                                            ) : (
                                                <>
                                                    <CustomSelect
                                                        value={displayComm}
                                                        onChange={(val) => {
                                                            if (!selectedQuarter) {
                                                                showToast('Please select a quarter first', 'warning');
                                                                return;
                                                            }
                                                            setCommunicatedStatuses(prev => ({
                                                                ...prev,
                                                                [selectedQuarter]: {
                                                                    ...(prev[selectedQuarter] || {}),
                                                                    [gId]: val
                                                                }
                                                            }));
                                                            if (val) {
                                                                setErrors(prev => {
                                                                    const next = { ...prev };
                                                                    delete next[`${gId}_communicated`];
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        options={COMMUNICATED_OPTIONS}
                                                        placeholder="Select"
                                                        error={!!errors[`${gId}_communicated`]}
                                                    />
                                                    {errors[`${gId}_communicated`] && (
                                                        <span className="error-message">{errors[`${gId}_communicated`]}</span>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() => (isRowSaved ? handleEditRow(goal, gId) : handleSaveRow(goal))}
                                                disabled={isGoalSubmitted || (!isRowSaved && !isSaveEnabled(goal, gId)) || loadingGoalId === gId}
                                                style={isGoalSubmitted ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                            >
                                                {isGoalSubmitted ? 'Saved' : (isRowSaved ? 'Edit' : (loadingGoalId === gId ? 'Saving...' : 'Save'))}
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() => handleSubmitRow(goal)}
                                                disabled={isGoalSubmitted || loadingGoalId === gId}
                                                className={`submit-btn ${isGoalSubmitted ? 'submitted' : ''}`}
                                            >
                                                {loadingGoalId === gId ? 'Submitting...' : isGoalSubmitted ? 'Submitted' : 'Submit'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                                    No assigned goals found for your account.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* TextModal for editing long text */}
            <TextModal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                onTextSubmit={handleTextSubmit}
                initialText={initialText}
                field={currentField}
            />

            {/* Instructions Modal */}
            <div className={`instructions-modal ${isInstructionsOpen ? 'open' : ''}`}>
                <h3>Goal Submission and Review Process</h3>
                <ol>
                    <li>Goals will be dynamically fetched in your dashboard once your manager assigns goals to you.</li>
                    <li>You need to select the particular quarter from the dropdown for which you are submitting the goal.</li>
                    <li>When entering your goal description, click the text input to use the full text editor or paste your notes.</li>
                    <li>To upload certifications or attachments, store the files in OneDrive and paste the shared link into the "Attachment" field.</li>
                    <li>After submitting a goal, your manager/lead will be notified automatically.</li>
                    <li>You can view all submitted goals in the "Submitted" section.</li>
                    <li>If you encounter any issues, please reach out to <a href="mailto:pms@tsqsinc.com">pms@tsqsinc.com</a> for assistance.</li>
                </ol>
                <button className="close-instructions" onClick={() => setInstructionsOpen(false)}>Close</button>
            </div>

            {/* Goal Information Pop-up Modal */}
            {infoGoal && (
                <div className="pms-overlay modal-overlay" onClick={() => setInfoGoal(null)}>
                    <div 
                        className="pms-modal modal-content goal-info-modal" 
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                            maxWidth: '520px', 
                            width: '92%', 
                            padding: '24px 28px', 
                            borderRadius: '10px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid var(--border, #e2e8f0)', paddingBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'rgba(234, 88, 12, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ea580c' }}>
                                    <FiInfo size={18} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary, #0f172a)' }}>
                                    Goal Information
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setInfoGoal(null)}
                                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary, #64748b)', padding: '4px', lineHeight: 1 }}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary, #64748b)', marginBottom: '6px' }}>
                                Goal Name
                            </label>
                            <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary, #1e293b)', padding: '10px 14px', backgroundColor: 'var(--bg-secondary, #f8fafc)', borderRadius: '6px', border: '1px solid var(--border, #e2e8f0)' }}>
                                {infoGoal.goal_name || infoGoal.goal_description || 'Goal'}
                            </div>
                        </div>

                        <div style={{ marginBottom: '22px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary, #64748b)', marginBottom: '6px' }}>
                                Goal Description
                            </label>
                            <div style={{ 
                                fontSize: '14px', 
                                lineHeight: '1.6', 
                                color: 'var(--text-primary, #334155)', 
                                padding: '12px 14px', 
                                backgroundColor: 'var(--bg-secondary, #f8fafc)', 
                                borderRadius: '6px', 
                                border: '1px solid var(--border, #e2e8f0)',
                                maxHeight: '220px',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {infoGoal.description ? (
                                    infoGoal.description
                                ) : (
                                    <span style={{ color: 'var(--text-muted, #94a3b8)', fontStyle: 'italic' }}>
                                        No detailed description provided for this goal.
                                    </span>
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                className="pms-btn"
                                onClick={() => setInfoGoal(null)}
                                style={{ 
                                    padding: '8px 24px', 
                                    backgroundColor: '#e67320', 
                                    borderColor: '#e67320', 
                                    color: '#ffffff', 
                                    borderRadius: '6px', 
                                    fontWeight: '600', 
                                    cursor: 'pointer' 
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoalAssessmentTable;
